class SpxLogoMarquee extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement | null} */
  #group = null;

  /** @type {HTMLElement[]} */
  #originalItems = [];

  /** @type {MediaQueryList | null} */
  #mediaQuery = null;

  /** @type {MediaQueryList | null} */
  #reducedMotionQuery = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #rebuildFrame = 0;

  connectedCallback() {
    if (this.#abortController) return;

    const track = this.querySelector('[data-spx-logo-track]');
    const group = this.querySelector('[data-spx-logo-group]');

    if (!(track instanceof HTMLElement) || !(group instanceof HTMLElement) || group.children.length === 0) return;

    this.#track = track;
    this.#group = group;
    this.#originalItems = /** @type {HTMLElement[]} */ (Array.from(group.children));
    this.#mediaQuery = window.matchMedia('(min-width: 750px)');
    this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;

    this.addEventListener('pointerdown', this.#handleInteractionStart, { signal });
    window.addEventListener('pointerup', this.#handleInteractionEnd, { signal });
    window.addEventListener('pointercancel', this.#handleInteractionEnd, { signal });
    this.#mediaQuery.addEventListener('change', this.#scheduleRebuild, { signal });
    this.#reducedMotionQuery.addEventListener('change', this.#scheduleRebuild, { signal });

    this.#resizeObserver = new ResizeObserver(this.#scheduleRebuild);
    this.#resizeObserver.observe(this);

    this.querySelectorAll('img').forEach((image) => {
      if (image instanceof HTMLImageElement && !image.complete) {
        image.addEventListener('load', this.#scheduleRebuild, { once: true, signal });
      }
    });

    this.#rebuild();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();

    if (this.#rebuildFrame) {
      window.cancelAnimationFrame(this.#rebuildFrame);
      this.#rebuildFrame = 0;
    }

    this.#clearClones();
    this.removeAttribute('data-interacting');
    this.#track = null;
    this.#group = null;
    this.#originalItems = [];
    this.#mediaQuery = null;
    this.#reducedMotionQuery = null;
    this.#resizeObserver = null;
    this.#abortController = null;
  }

  #handleInteractionStart = () => {
    if (this.dataset.running === 'true') this.setAttribute('data-interacting', '');
  };

  #handleInteractionEnd = () => {
    this.removeAttribute('data-interacting');
  };

  #scheduleRebuild = () => {
    if (this.#rebuildFrame) window.cancelAnimationFrame(this.#rebuildFrame);

    this.#rebuildFrame = window.requestAnimationFrame(() => {
      this.#rebuildFrame = 0;
      this.#rebuild();
    });
  };

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #cloneItem(item) {
    const clone = /** @type {HTMLElement} */ (item.cloneNode(true));

    clone.dataset.spxLogoCloneItem = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('[id], [data-shopify-editor-block]').forEach((element) => {
      element.removeAttribute('id');
      element.removeAttribute('data-shopify-editor-block');
    });
    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });

    return clone;
  }

  #clearClones() {
    this.#group?.querySelectorAll('[data-spx-logo-clone-item]').forEach((item) => item.remove());
    this.#track?.querySelectorAll('[data-spx-logo-clone-group]').forEach((group) => group.remove());
  }

  #rebuild() {
    const track = this.#track;
    const group = this.#group;
    const mediaQuery = this.#mediaQuery;
    const reducedMotionQuery = this.#reducedMotionQuery;

    if (!track || !group || !mediaQuery || !reducedMotionQuery || this.#originalItems.length === 0) return;

    this.#clearClones();

    const autoscroll = mediaQuery.matches
      ? this.dataset.autoscrollDesktop === 'true'
      : this.dataset.autoscrollMobile === 'true';
    const shouldRun = autoscroll && !reducedMotionQuery.matches;

    this.dataset.running = String(shouldRun);
    this.style.removeProperty('--spx-logo-marquee-distance');
    this.style.removeProperty('--spx-logo-marquee-duration');

    if (!shouldRun) return;

    const trackStyles = window.getComputedStyle(track);
    const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
    const minimumGroupWidth = this.clientWidth + gap;
    let cloneRound = 0;
    const maximumCloneRounds = Math.max(50, Math.ceil(500 / this.#originalItems.length));

    while (group.getBoundingClientRect().width < minimumGroupWidth && cloneRound < maximumCloneRounds) {
      this.#originalItems.forEach((sourceItem) => {
        group.appendChild(this.#cloneItem(sourceItem));
      });

      cloneRound += 1;
    }

    const repeatedGroup = /** @type {HTMLElement} */ (group.cloneNode(true));
    repeatedGroup.dataset.spxLogoCloneGroup = '';
    repeatedGroup.setAttribute('aria-hidden', 'true');
    repeatedGroup.removeAttribute('data-spx-logo-group');
    repeatedGroup.querySelectorAll('[id], [data-shopify-editor-block]').forEach((element) => {
      element.removeAttribute('id');
      element.removeAttribute('data-shopify-editor-block');
    });
    repeatedGroup.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    track.appendChild(repeatedGroup);

    const distance = group.getBoundingClientRect().width + gap;
    const configuredSpeed = Number.parseFloat(this.dataset.speed ?? '');
    const speed = Number.isFinite(configuredSpeed) && configuredSpeed > 0 ? configuredSpeed : 60;

    this.style.setProperty('--spx-logo-marquee-distance', `${distance}px`);
    this.style.setProperty('--spx-logo-marquee-duration', `${distance / speed}s`);
  }
}

if (!customElements.get('spx-logo-marquee')) {
  customElements.define('spx-logo-marquee', SpxLogoMarquee);
}
